import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { AnyAction, Middleware } from 'redux';
import cartReducer from './cartSlice';
import productsReducer from './productSlice';
import checkoutReducer from './checkoutSlice';
import mpesapaymentsReducer from './mpesaPaymentSlice';
import orderDetailsReducer from './orderDetailsSlice';
import partialpaymentsReducer from './partialPaymentsSlice';
import locationReducer from './locationSlice';
import settingsReducer from './SettingsSlice';
import authReducer from './AuthSlice';
import ordersReducer from './OrdersSlice';
import productSortReducer from './productSortSlice';
import backofficeMpesaConfigReducer from './backoffice/mpesaConfigSlice';
import mpesaConfigReducer from './mpesaConfig';
import backofficeDashboardReducer from './backoffice/dashboardSlice';
import backofficeReportsReducer from './backoffice/reportsSlice';
import backofficeTargetsReducer from './backoffice/targetsSlice';
import backofficeProductsReducer from './backoffice/productsSlice';
import backofficeReportsTableReducer from './backoffice/reportsTableSlice';
import backofficeSummaryReducer from './backoffice/summarySlice';
import backofficeOwnerAuthReducer from './backoffice/ownerAuthSlice';
import backofficeEmployeesReducer from './backoffice/employeesSlice';
import backofficeOrdersReducer from './backoffice/ordersSlice';
import backofficeExpensesReducer from './backoffice/expensesSlice';
import backofficeEmployeePaymentsReducer from './backoffice/employeePaymentsSlice';
import backofficeSalesReducer from './backoffice/salesSlice';
import backofficeBillingReducer from './backoffice/billingSlice';
import backofficeCompanyProfileReducer from './backoffice/companyProfileSlice';
import backofficeSuppliersReducer from './backoffice/suppliersSlice';
import backofficeCustomersReducer from './backoffice/customersSlice';
import backofficeCustomerOrdersReducer from './backoffice/customerOrdersSlice';
import securityRuntimeReducer from './securityRuntimeSlice';
import uiReducer from './uiSlice';
import notificationsReducer from './notificationsSlice';
import backofficeRecentTransactionsReducer from './backoffice/recentTransactionsSlice';
import employeeRecentTransactionsReducer from './backoffice/employeeRecentTransactionsSlice';
import assignableStaffReducer from './assignableStaffSlice';
import ownerActivationReducer, {
  migrateOwnerActivationState,
} from './ownerActivationSlice';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  persistReducer,
  persistStore,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import { createLogger } from 'redux-logger';
import ChatwootService from '../services/ChatwootService';

export const LOGOUT = 'LOGOUT';

export const logoutAction = () => {
  void ChatwootService.reset();
  return {
    type: LOGOUT,
  };
};

const reducers = combineReducers({
  products: productsReducer,
  cart: cartReducer,
  checkout: checkoutReducer,
  orders: ordersReducer,
  mpesapayments: mpesapaymentsReducer,
  orderdetails: orderDetailsReducer,
  partialpayments: partialpaymentsReducer,
  settings: settingsReducer,
  auth: authReducer,
  location: locationReducer,
  productSort: productSortReducer,
  mpesaConfig: mpesaConfigReducer,
  backofficeMpesaConfig: backofficeMpesaConfigReducer,
  backofficeDashboard: backofficeDashboardReducer,
  backofficeReports: backofficeReportsReducer,
  backofficeTargets: backofficeTargetsReducer,
  backofficeProducts: backofficeProductsReducer,
  backofficeOwnerAuth: backofficeOwnerAuthReducer,
  backofficeEmployees: backofficeEmployeesReducer,
  backofficeEmployeePayments: backofficeEmployeePaymentsReducer,
  backofficeOrders: backofficeOrdersReducer,
  backofficeExpenses: backofficeExpensesReducer,
  backofficeSales: backofficeSalesReducer,
  backofficeSuppliers: backofficeSuppliersReducer,
  backofficeCustomers: backofficeCustomersReducer,
  backofficeCustomerOrders: backofficeCustomerOrdersReducer,
  backofficeReportsTable: backofficeReportsTableReducer,
  backofficeBilling: backofficeBillingReducer,
  backofficeSummary: backofficeSummaryReducer,
  backofficeCompanyProfile: backofficeCompanyProfileReducer,
  // Runtime-only security state (explicitly NOT persisted)
  securityRuntime: securityRuntimeReducer,
  // Runtime-only UI state (explicitly NOT persisted)
  ui: uiReducer,
  notifications: notificationsReducer,
  backofficeRecentTransactions: backofficeRecentTransactionsReducer,
  employeeRecentTransactions: employeeRecentTransactionsReducer,
  assignableStaff: assignableStaffReducer,
  ownerActivation: ownerActivationReducer,
});

const logger = createLogger({
  collapsed: true,
});

/**
 * Defensive normalization for the persisted owner-activation aggregate.
 * Rehydration of missing/malformed/future-versioned snapshots can never
 * create eligibility or task completion.
 */
const ownerActivationTransform = createTransform(
  inbound => inbound,
  outbound => migrateOwnerActivationState(outbound),
  { whitelist: ['ownerActivation'] },
);

const persistConfig = {
  key: 'root',
  storage: AsyncStorage,
  stateReconciler: autoMergeLevel2,
  transforms: [ownerActivationTransform],
  whitelist: [
    'settings',
    'auth',
    'location',
    'mpesaConfig',
    'productSort',
    'backofficeDashboard',
    'backofficeEmployees',
    'backofficeReports',
    'backofficeTargets',
    'backofficeProducts',
    'backofficeOwnerAuth',
    'backofficeOrders',
    'backofficeExpenses',
    'backofficeSales',
    'backofficeSuppliers',
    'backofficeCustomers',
    'backofficeReportsTable',
    'backofficeBilling',
    'backofficeSummary',
    'backofficeMpesaConfig',
    'backofficeCompanyProfile',
    'notifications',
    'backofficeRecentTransactions',
    'employeeRecentTransactions',
    'ownerActivation',
  ],
  blacklist: ['checkout', 'cart', 'products', 'ui', 'orders'],
};

type RootState = ReturnType<typeof reducers>;
const PERSIST_STORAGE_KEY = `persist:${persistConfig.key}`;

const rootReducer = (
  state: RootState | undefined,
  action: AnyAction,
): RootState => {
  if (action.type === LOGOUT) {
    // Clear the persisted state
    void AsyncStorage.removeItem(PERSIST_STORAGE_KEY);

    // Return the initial state
    return reducers(undefined, action);
  }
  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: { warnAfter: 128 },
      serializableCheck: {
        warnAfter: 128,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat((__DEV__ ? [logger] : []) as Middleware[]),
});

export type AppDispatch = typeof store.dispatch;

/**
 * Singleton persistor. Durable-eligibility writes (owner setup handoff) await
 * `persistor.flush()` on this instance; creating additional persistors per
 * render would break flush guarantees.
 */
export const persistor = persistStore(store);

export default store;
